import { useSelector } from "@tanstack/react-store";
import type { RowData } from "@tanstack/react-table";
import classNames from "classnames";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Tag } from "@design-system/Tag/Tag";
import { resolveColumnMeta } from "../columns/columnKinds";
import { useDataGridContext } from "../DataGridContext";
import type { DataGridInstance } from "../types";
import styles from "./toolbar.module.scss";

type FilterChipPrimitive = string | number | boolean;

/** A column filter's value as chips read it: null for values they can't show, such as objects. */
export type DataGridFilterChipValue =
    | FilterChipPrimitive
    | FilterChipPrimitive[]
    | null;

const isChipPrimitive = <TValue,>(
    value: TValue | FilterChipPrimitive,
): value is FilterChipPrimitive =>
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean";

const toChipValue = <TValue,>(value: TValue): DataGridFilterChipValue => {
    if (isChipPrimitive(value)) {
        return value;
    }
    return Array.isArray(value) ? value.filter(isChipPrimitive) : null;
};

interface DataGridFilterChipsProps {
    /** Chip text per column; defaults to the value joined with commas. */
    formatValue?: (input: {
        columnId: string;
        value: DataGridFilterChipValue;
    }) => string;
    className?: string;
}

const removeFilter = <TData extends RowData>(
    table: DataGridInstance<TData>["table"],
    filterId: string,
) =>
    table.setColumnFilters((filters) =>
        filters.filter(({ id }) => id !== filterId),
    );

const defaultFormatValue = ({
    value,
}: {
    value: DataGridFilterChipValue;
}): string => {
    if (Array.isArray(value)) {
        return value.join(", ");
    }
    return value === null ? "" : String(value);
};

interface FilterChipListProps<
    TData extends RowData,
> extends DataGridFilterChipsProps {
    instance: DataGridInstance<TData>;
    clearFilters: () => void;
}

const FilterChipList = <TData extends RowData>({
    instance,
    clearFilters,
    formatValue = defaultFormatValue,
    className,
}: Readonly<FilterChipListProps<TData>>) => {
    const { table } = instance;
    const columnFilters = useSelector(table.atoms.columnFilters);
    if (columnFilters.length === 0) {
        return null;
    }
    return (
        <HStack
            gap={6}
            align="center"
            wrap
            className={classNames(styles.filterChips, className)}
        >
            {columnFilters.map((filter) => {
                const column = table.getColumn(filter.id);
                const label = column
                    ? resolveColumnMeta(column.columnDef, column.id).headerLabel
                    : filter.id;
                const text = formatValue({
                    columnId: filter.id,
                    value: toChipValue(filter.value),
                });
                return (
                    <Tag
                        key={filter.id}
                        onRemove={() => removeFilter(table, filter.id)}
                        removeLabel={`Remove ${label} filter`}
                    >
                        {text === "" ? label : `${label}: ${text}`}
                    </Tag>
                );
            })}
            <Button variant="secondary" size="sm" onClick={clearFilters}>
                Clear filters
            </Button>
        </HStack>
    );
};

/**
 * One removable chip per active column filter, plus Clear filters (which
 * also clears the search). Renders nothing while no column filter is set.
 */
export const DataGridFilterChips = (
    props: Readonly<DataGridFilterChipsProps>,
) => {
    const { visit, clearFilters } = useDataGridContext();
    return visit((instance) => (
        <FilterChipList
            {...props}
            instance={instance}
            clearFilters={clearFilters}
        />
    ));
};
